import { cn } from "../../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RatingDistributionChartProps {
  /** Array of 5 entries, one per star level (index 0 = 1 star, index 4 = 5 stars) */
  data: Array<{ rating: number; count: number }>;
  className?: string;
}

// ── Star icon ─────────────────────────────────────────────────────────────────

function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="text-amber-400 shrink-0">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RatingDistributionChart({
  data,
  className,
}: RatingDistributionChartProps) {
  const sorted = [...data].sort((a, b) => b.rating - a.rating);
  const total = sorted.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className={cn("flex flex-col gap-2", className)} role="list" aria-label="Rating distribution">
      {sorted.map(({ rating, count }) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;

        return (
          <div
            key={rating}
            role="listitem"
            className="flex items-center gap-2.5"
          >
            {/* Star label */}
            <div className="flex items-center gap-0.5 w-8 shrink-0">
              <span className="text-xs text-content-secondary tabular-nums">{rating}</span>
              <StarIcon />
            </div>

            {/* Bar track */}
            <div className="relative flex-1 h-2.5 rounded-full bg-surface-sunken overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-amber-400 transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </div>

            {/* Percentage */}
            <span className="w-9 shrink-0 text-right text-xs text-content-tertiary tabular-nums">
              {pct}%
            </span>

            {/* Count */}
            <span className="w-8 shrink-0 text-right text-xs text-content-tertiary tabular-nums">
              ({count})
            </span>
          </div>
        );
      })}
    </div>
  );
}

RatingDistributionChart.displayName = "RatingDistributionChart";
